import React, { useState, useEffect } from 'react';
import axios , {AxiosError} from 'axios';
import { 
  Plus, Search, Edit3, Eye, Trash2, X, Zap 
} from 'lucide-react';

import BaseModal from '../../../components/BaseModal/BaseModal';
import Button from '../../../components/Button/Button';
import StatusBadge from '../../../components/StatusBadge/StatusBadge';
import styles from '../Subscriptions/Subscriptions.module.css'; 
import toast from 'react-hot-toast';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const API_BASE = `${BASE_URL}/module`;

interface Module {
  id: string;
  name: string;
  status: 'Active' | 'Suspended';
  created_at?: string;
}


type ErrResponse = {
  success?: boolean;
  message?: string;
};

const ManageModule: React.FC = () => {
  const [modules, setModules] = useState<Module[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingModule, setEditingModule] = useState<Module | null>(null);
  const [detailModule, setDetailModule] = useState<Module | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Module | null>(null);
  
  const [form, setForm] = useState({ name: '', status: 'Active' });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  // --- API Functions ---
  const fetchModules = async () => {
    try {
      const res = await axios.get(`${API_BASE}/get`);
      setModules(res.data.data || res.data);
    } catch (e) { console.error('Fetch error', e); }
  };

  useEffect(() => { fetchModules(); }, []);

  useEffect(() => {
    if (editingModule) {
      setForm({ name: editingModule.name, status: editingModule.status });
    } else {
      setForm({ name: '', status: 'Active' });
    }
  }, [editingModule, formOpen]);

  const handleSubmit = async () => {
    if (!form.name.trim()) {
    toast.error('Module name is required');
    return;
  }
    setLoading(true);
    try {
      if (editingModule) {
        await axios.put(`${API_BASE}/update/${editingModule.id}`, form);
      } else {
        await axios.post(`${API_BASE}/add`, form);
      }
      setFormOpen(false);
      setEditingModule(null);
      fetchModules();
    } catch (err) { 
      
const error = err as AxiosError<ErrResponse>;
    const msg =
      error.response?.data?.message ??
      error.message ??
      'Something went wrong';
       toast.error(msg);    }
     finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_BASE}/delete/${deleteTarget.id}`);
      setDeleteTarget(null);
      fetchModules();
    } catch (e) { console.error('Delete error', e); }
  };

  const filteredModules = modules.filter(m => 
    m.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className={styles.page}>
      {/* Header Section */}
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Manage Modules</h1>
          <p className={styles.pageSubtitle}>Configure and manage system modules</p>
        </div>
        <div className={styles.headerActions}>
          <Button
            variant="primary"
            size="md"
            icon={<Plus size={14} />}
            onClick={() => { setEditingModule(null); setFormOpen(true); }}
          >
            Add Module
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statTop}>
            <span className={styles.statLabel}>Total Modules</span>
            <Zap size={15} />
          </div>
          <div className={styles.statValue}>{modules.length}</div>
        </div>
      </div>

      {/* Toolbar / Search */}
      <div className={styles.toolbar} style={{ marginBottom: '20px' }}>
        <div className={styles.searchWrap}>
          <Search size={14} className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            placeholder="Search modules..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Cards Grid - Direct Render */}
      <div className={styles.plansGrid}>
        {filteredModules.map((m) => (
          <div key={m.id} className={styles.planCard}>
            <div className={styles.planCardHeader}>
              <div className={styles.planCardNameRow}>
                <h3 className={styles.planCardName}>{m.name}</h3>
                <StatusBadge 
                  variant={m.status.toLowerCase() === 'active' ? 'active' : 'suspended'} 
                  size="sm" 
                />
              </div>
            </div>
            {/* <div className={styles.planCardLimits}>
               <div className={styles.planCardLimitRow}>
                  <span className={styles.planCardLimitLabel}>Module ID</span>
                  <span className={styles.planCardLimitValue}>{m.id}</span>
               </div>
            </div> */}
            <div className={styles.planCardActions}>
              <button className={styles.planCardBtn} onClick={() => setDetailModule(m)}><Eye size={13} /> View</button>
              <button className={styles.planCardBtn} onClick={() => { setEditingModule(m); setFormOpen(true); }}><Edit3 size={13} /> Edit</button>
              <button className={`${styles.planCardBtn} ${styles.planCardBtnDanger}`} onClick={() => setDeleteTarget(m)}><Trash2 size={13} /> Delete</button>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Modal */}
      <BaseModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        title={editingModule ? "Edit Module" : "Add New Module"}
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleSubmit} disabled={loading}>
              {loading ? 'Saving...' : editingModule ? 'Update Module' : 'Add Module'}
            </Button>
          </>
        }
      >
        <div className={styles.formGrid}>
          <div className={styles.formGroup}>
            <label className={styles.label}>Module Name</label>
            <input
              className={styles.input}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>Status</label>
            <select
              className={styles.input}
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as any })}
            >
              <option value="Active">Active</option>
              <option value="Suspended">Suspended</option>
            </select>
          </div>
        </div>
      </BaseModal>

      {/* Delete Confirmation Modal */}
      <BaseModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Module"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="danger" onClick={handleDelete}>Delete</Button>
          </>
        }
      >
        <p>Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?</p>
      </BaseModal>

      {/* View Drawer - Inline Logic */}
      {detailModule && (
        <>
          <div className={styles.drawerBackdrop} onClick={() => setDetailModule(null)} />
          <aside className={styles.drawer}>
            <div className={styles.drawerHeader}>
              <div><h3 className={styles.drawerName}>{detailModule.name}</h3><span className={styles.drawerId}>ID: {detailModule.id}</span></div>
              <button className={styles.drawerClose} onClick={() => setDetailModule(null)}><X size={14} /></button>
            </div>
            <div className={styles.drawerBody}>
              <div className={styles.drawerRow}><span className={styles.drawerRowLabel}>Status</span><span className={styles.drawerRowValue}>{detailModule.status}</span></div>
              {/* <div className={styles.drawerRow}><span className={styles.drawerRowLabel}>Created At</span><span className={styles.drawerRowValue}>{detailModule.created_at || 'N/A'}</span></div> */}
            </div>
          </aside>
        </>



      )}
    </div>
  );
};

export default ManageModule;